import { useCallback, useEffect, useState } from 'react';
import axios from '../../../axiosConfig';

/**
 * List + mutate Other Income entries within a period. Calls
 * /api/finance/income. Returns rows with `amount` already coerced
 * to Number so consumers don't have to handle Prisma Decimal shapes.
 */
export default function useOtherIncome(period = '30d') {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/finance/income', { params: { period: p } });
      setItems(res.data?.items || []);
      setTotal(Number(res.data?.total || 0));
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load other income';
      setError(msg);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(period);
  }, [period, fetchAll]);

  /* Create. Returns the new row on success, throws on failure so the
     form can show the toast in a consistent place. */
  const create = useCallback(async (payload) => {
    const res = await axios.post('/finance/income', payload);
    const item = res.data?.item;
    if (item) setItems((prev) => [item, ...prev]);
    if (item) setTotal((prev) => prev + Number(item.amount || 0));
    return item;
  }, []);

  const update = useCallback(async (id, payload) => {
    const res = await axios.put(`/finance/income/${id}`, payload);
    const item = res.data?.item;
    if (item) {
      setItems((prev) => prev.map((it) => (it.id === id ? item : it)));
      // Re-sum since amount may have changed
      setTotal((prev) => {
        const old = items.find((it) => it.id === id);
        const delta = Number(item.amount || 0) - Number(old?.amount || 0);
        return prev + delta;
      });
    }
    return item;
  }, [items]);

  const remove = useCallback(async (id) => {
    await axios.delete(`/finance/income/${id}`);
    setItems((prev) => prev.filter((it) => it.id !== id));
    const removed = items.find((it) => it.id === id);
    if (removed) setTotal((prev) => prev - Number(removed.amount || 0));
  }, [items]);

  return {
    items,
    total,
    loading,
    error,
    refetch: () => fetchAll(period),
    create,
    update,
    remove,
  };
}
